import { useState, useMemo } from "react";
import { THEME_COLOR } from "../config/club";
import SessionStandingsModal from "./admin/SessionStandingsModal";
const MatchHistorySection = ({ matchHistory, renderTeams, onEditMatch }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [showStandings, setShowStandings] = useState(false);
  //console.log(matchHistory)
  // ✅ Multi-priority sorting
  const [sortPriority, setSortPriority] = useState([
    "wins",
    "scored",
    "diff"
  ]);

  const metricOptions = [
    { label: "Wins", value: "wins" },
    { label: "Losses", value: "losses" },
    { label: "Points", value: "scored" },
    { label: "Diff", value: "diff" },
    { label: "Win %", value: "winRate" }
  ];

  const getAvailableOptions = (index) => {
    return metricOptions.filter(
      opt =>
        !sortPriority.includes(opt.value) ||
        sortPriority[index] === opt.value
    );
  };

  const reversedHistory = useMemo(() => [...matchHistory].reverse(), [matchHistory]);

  const filteredHistory = useMemo(() => {
    if (!searchTerm.trim()) return reversedHistory;
    const keyword = searchTerm.toLowerCase();
    return reversedHistory.filter((entry) => {
      const teamText =
        (entry.match?.[0]?.name || "") + " " +
        (entry.match?.[1]?.name || "") + " " +
        (entry.match?.[2]?.name || "") + " " +
        (entry.match?.[3]?.name || "");
      const courtText = String(entry.court || "").toLowerCase();
      return teamText.toLowerCase().includes(keyword) || courtText.includes(keyword);
    });
  }, [reversedHistory, searchTerm]);

  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);

  const paginated = useMemo(() =>
    filteredHistory.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [filteredHistory, currentPage, itemsPerPage]
  );

  // ✅ Build standings
  const standings = useMemo(() => {
  const stats = {};

  matchHistory.forEach(entry => {
    if (!entry.score || !entry.match) return;

    const [a, b] = entry.score.split("-").map(Number);

    const teamA = entry.match.slice(0, 2);
    const teamB = entry.match.slice(2, 4);

    const updatePlayer = (player, win, scored, conceded) => {
      const { name, photoURL } = player;

      if (!stats[name]) {
        stats[name] = {
          name,
          photoURL: photoURL || null, // 👈 ADD HERE
          games: 0,
          wins: 0,
          losses: 0,
          scored: 0,
          conceded: 0,
        };
      }

      stats[name].games += 1;
      stats[name].wins += win ? 1 : 0;
      stats[name].losses += win ? 0 : 1;
      stats[name].scored += scored;
      stats[name].conceded += conceded;
    };

    const teamAWon = a > b;

    teamA.forEach(p => updatePlayer(p, teamAWon, a, b));
    teamB.forEach(p => updatePlayer(p, !teamAWon, b, a));
  });

  return Object.values(stats).map(p => ({
    ...p,
    diff: p.scored - p.conceded,
    winRate: p.games ? (p.wins / p.games) * 100 : 0,
  }));

}, [matchHistory]);


  // ✅ Dynamic multi-level sort
  const sortedStandings = useMemo(() => {
    return [...standings].sort((a, b) => {
      for (let key of sortPriority) {

        // 👇 Special rule: fewer losses is better
        if (key === "losses") {
          if (a[key] !== b[key]) {
            return a[key] - b[key]; // ASCENDING
          }
        } else {
          if (a[key] !== b[key]) {
            return b[key] - a[key]; // DESCENDING
          }
        }

      }
      return 0;
    });
  }, [standings, sortPriority]);


  return (
    <div className="card border-0 shadow-sm rounded-4 overflow-hidden mt-4 bg-card">

      <div style={{ height: '4px', backgroundColor: THEME_COLOR, opacity: 0.8 }}></div>

      <div className="card-header bg-card border-0 pt-4 pb-3 px-4">
        {/* TOP ROW: Title & Main Action */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-black mb-0" style={{ letterSpacing: '-0.5px' }}>Match History</h5>
          <button
            className="btn btn-sm rounded-pill fw-bold px-3 shadow-sm"
            style={{ backgroundColor: THEME_COLOR, color: "white", fontSize: '0.8rem' }}
            onClick={() => setShowStandings(true)}
          >
            <i className="bi bi-trophy-fill me-2"></i>
            Standings
          </button>
        </div>

        {/* BOTTOM ROW: Filters & Search */}
        <div className="d-flex flex-column flex-md-row gap-2">
          {/* Search Bar - Flex Grow to take available space */}
          <div className="input-group input-group-sm bg-surface border rounded-pill px-3 py-1 flex-grow-1 shadow-none"
            style={{ borderColor: "var(--border-color)" }}>
            <span className="input-group-text bg-transparent border-0 text-muted p-0 me-2">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control bg-transparent border-0 shadow-none p-0"
              placeholder="Search player or court..."
              style={{ fontSize: '0.85rem', height: '24px' }}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
            {searchTerm && (
              <button
                className="btn btn-link text-muted p-0 border-0 shadow-none"
                onClick={() => setSearchTerm('')}
              >
                <i className="bi bi-x-circle-fill"></i>
              </button>
            )}
          </div>

          {/* Items Per Page Select */}
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted d-none d-lg-block" style={{ fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase' }}>Show:</span>
            <select
              className="form-select form-select-sm rounded-pill border shadow-sm w-auto fw-bold"
              style={{ fontSize: '0.75rem', cursor: 'pointer', height: '34px', minWidth: '100px' }}
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={5}>5 / page</option>
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
            </select>
          </div>
        </div>

        <style>{`
    .transition-all { transition: all 0.2s ease; }
    .transition-all:hover { transform: translateY(-1px); opacity: 0.9; }
    
    /* Remove the default focus blue outline on the select and input */
    .form-select:focus, .form-control:focus {
      outline: none !important;
      box-shadow: none !important;
    }
  `}</style>
      </div>

      <div className="card-body p-0">
        {/* Desktop Table View */}
        <div className="d-none d-lg-block">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr className="text-muted small text-uppercase fw-bold" style={{ background: "var(--surface-bg)", letterSpacing: '0.5px' }}>
                <th className="ps-4 py-3 border-0">ID</th>
                <th className="border-0">Matchup</th>
                <th className="border-0 text-center">Court</th>
                <th className="border-0 text-center">Time</th>
                <th className="border-0 text-center">Duration</th>
                <th className="border-0 text-end pe-4">Score</th>
                <th className="border-0 text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody className="border-0">
              {paginated.map((entry, idx) => {
                const durationMins = Math.floor(entry.duration / 60000);
                const matchNumber = filteredHistory.length - ((currentPage - 1) * itemsPerPage + idx);

                return (
                  <tr key={idx} className="border-bottom-0">
                    <td className="ps-4 py-3 fw-bold text-muted border-0 opacity-50">#{matchNumber}</td>
                    <td className="border-0">
                      <div className="fw-bold">{renderTeams(entry.match)}</div>
                    </td>
                    <td className="text-center border-0">
                      <span className="px-3 py-1 rounded-pill fw-bold" style={{ backgroundColor: '#F3E8FF', color: THEME_COLOR, fontSize: '0.7rem' }}>
                        C{entry.court}
                      </span>
                    </td>
                    <td className="text-center border-0 small text-muted">
                      <span className="fw-medium">{new Date(entry.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      <span className="mx-2 opacity-25">|</span>
                      <span>{new Date(entry.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </td>
                    <td className="text-center border-0">
                      <div className="d-flex align-items-center justify-content-center gap-1 small fw-bold text-muted">
                        <i className="bi bi-hourglass-split"></i>
                        {durationMins}m
                      </div>
                    </td>
                    <td className="text-end pe-4 border-0">
                      {entry.score ? (
                        <span className="badge rounded-3 px-3 py-2 shadow-sm" style={{ backgroundColor: THEME_COLOR, fontSize: '0.8rem' }}>{entry.score}</span>
                      ) : (
                        <span className="text-muted small fw-medium bg-light px-2 py-1 rounded">End</span>
                      )}
                    </td>
                    <td className="text-end pe-4 border-0">
                      <button
                        className="btn btn-sm rounded-pill"
                        style={{
                          background: "var(--surface-bg)",
                          color: "var(--text-color)",
                          borderColor: "var(--border-color)"
                        }}
                        onClick={() => onEditMatch(entry)}
                      >
                        <i className="bi bi-pencil-square me-1"></i>
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className="d-lg-none p-3 d-flex flex-column gap-3 bg-light bg-opacity-50">
          {paginated.map((entry, idx) => (
            <div className="card border-0 rounded-4 shadow-sm" key={idx}>
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <span className="small fw-black text-muted opacity-50">#{filteredHistory.length - ((currentPage - 1) * itemsPerPage + idx)}</span>
                  <span className="badge rounded-pill" style={{ backgroundColor: '#F3E8FF', color: THEME_COLOR }}>Court {entry.court}</span>
                </div>
                <div className="mb-3 fw-bold h6" style={{ color: "var(--text-color)" }}>{renderTeams(entry.match)}</div>
                <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                  <div className="small text-muted fw-medium">
                    <i className="bi bi-clock-history me-1"></i>
                    {Math.floor(entry.duration / 60000)}m
                  </div>
                  <div className="fw-black" style={{ color: THEME_COLOR }}>{entry.score || "FINALIZED"}</div>
                </div>
                <button
                  className="btn btn-sm w-100 rounded-pill mt-3"
                  style={{ backgroundColor: THEME_COLOR, color: "white" }}
                  onClick={() => onEditMatch(entry)}
                >
                  <i className="bi bi-pencil-square me-2"></i>
                  Edit Match
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredHistory.length === 0 && (
          <div className="text-center py-5">
            <i className="bi bi-search fs-1 opacity-25 d-block mb-2" style={{ color: THEME_COLOR }}></i>
            <p className="text-muted fw-medium">No results found for "{searchTerm}"</p>
          </div>
        )}
      </div>

      {/* Pagination: Styled like JoinClub buttons */}
      {totalPages > 1 && (
        <div className="card-footer border-0 py-4">
          <nav className="d-flex justify-content-center">
            <ul className="pagination pagination-sm gap-2 mb-0 border-0">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button className="page-link rounded-circle border-0" onClick={() => setCurrentPage(currentPage - 1)}>
                  <i className="bi bi-chevron-left"></i>
                </button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                  <button
                    className={`page-link rounded-circle border-0 mx-0 fw-bold d-flex align-items-center justify-content-center`}
                    style={{
                      width: '32px',
                      height: '32px',
                      // backgroundColor: currentPage === i + 1 ? THEME_COLOR : '#F3F4F6',
                      // color: currentPage === i + 1 ? 'white' : '#4B5563'
                      background: `var(--surface-bg)`,
                      color: `var(--text-color)`
                    }}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                <button className="page-link rounded-circle border-0" onClick={() => setCurrentPage(currentPage + 1)}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      <SessionStandingsModal
        show={showStandings}
        onClose={() => setShowStandings(false)}
        sortedStandings={sortedStandings}
        sortPriority={sortPriority}
        setSortPriority={setSortPriority}
        getAvailableOptions={getAvailableOptions}
        THEME_COLOR={THEME_COLOR}
      />

    </div>
  );
};

export default MatchHistorySection;
